/**
 * EEOC Scenario Reference Book
 * Comprehensive training guide with mock test scenarios and sample answers
 * Users can study this before taking the mock test or assessment
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import {
  Shield,
  ArrowLeft,
  BookOpen,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MessageSquare,
  Lightbulb,
  User,
  Users,
  Baby,
  Calendar,
  Globe,
  Heart,
  Accessibility,
  ThumbsUp,
  ThumbsDown,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Comprehensive EEOC Scenarios with multiple response options
const SCENARIO_CATEGORIES = [
  {
    id: 'age',
    title: 'Age-Related Scenarios',
    icon: Calendar,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    description: 'Handling situations where candidates reveal age-related information',
    scenarios: [
      {
        id: 'age-1',
        candidateSays: "I graduated from college in 1990, so I've been in this field for over 30 years now.",
        context: 'Candidate reveals their approximate age through graduation year',
        wrongResponses: [
          "Wow, you must be close to retirement then!",
          "That's a long time - can you keep up with the younger staff?",
          "30 years? You've been doing this longer than I've been alive!"
        ],
        goodResponses: [
          {
            response: "That's great experience. Can you tell me about a recent project where you applied modern technologies or methodologies?",
            explanation: "Acknowledges experience positively while redirecting to current, relevant skills"
          },
          {
            response: "Thank you for sharing your background. What aspects of this role excite you the most given your extensive experience?",
            explanation: "Focuses on motivation and role fit rather than age"
          },
          {
            response: "I appreciate you sharing that. Let's discuss how your experience aligns with the specific challenges of this position.",
            explanation: "Neutral acknowledgment followed by job-focused discussion"
          }
        ],
        keyTakeaway: "Never comment on length of time or proximity to retirement. Focus on relevant, recent experience."
      },
      {
        id: 'age-2',
        candidateSays: "I'm 58 years old, and I know some companies prefer younger employees, but I have so much to offer.",
        context: 'Candidate directly states their age and expresses concern about age discrimination',
        wrongResponses: [
          "Don't worry, we don't discriminate based on age here.",
          "58 isn't that old! You still have plenty of working years left.",
          "I understand your concern - we do have a young team."
        ],
        goodResponses: [
          {
            response: "We evaluate all candidates based on their qualifications and ability to perform the job. Tell me about your relevant skills for this role.",
            explanation: "Reassures through focus on merit-based evaluation without acknowledging age as a factor"
          },
          {
            response: "We're focused on finding the best fit for this position. What specific experiences make you confident you'd excel in this role?",
            explanation: "Redirects to qualifications while avoiding the age topic"
          },
          {
            response: "Let's focus on what you can bring to this role. Can you walk me through your most relevant accomplishments?",
            explanation: "Direct pivot to job-related discussion"
          }
        ],
        keyTakeaway: "Don't engage with age concerns directly. Redirect to qualifications and job requirements."
      }
    ]
  },
  {
    id: 'pregnancy',
    title: 'Pregnancy & Family Planning',
    icon: Baby,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    description: 'Handling pregnancy disclosures and family-related information',
    scenarios: [
      {
        id: 'pregnancy-1',
        candidateSays: "I should mention that I'm currently pregnant, about 5 months along. I want to be upfront about that.",
        context: 'Candidate voluntarily discloses pregnancy status',
        wrongResponses: [
          "Congratulations! When is your due date?",
          "How long do you plan to take off for maternity leave?",
          "Will you be able to handle the job responsibilities while pregnant?"
        ],
        goodResponses: [
          {
            response: "Thank you for sharing. Our focus today is on your qualifications for the role. Can you tell me about your experience with [job requirement]?",
            explanation: "Politely acknowledges without follow-up questions, redirects to qualifications"
          },
          {
            response: "I appreciate your openness. Let's continue discussing the position. What interests you most about this opportunity?",
            explanation: "Brief acknowledgment followed by job-focused question"
          },
          {
            response: "Thank you. We evaluate candidates based on their skills and qualifications. What relevant experience do you bring to this role?",
            explanation: "Reinforces merit-based evaluation while redirecting"
          }
        ],
        keyTakeaway: "Never ask follow-up questions about pregnancy. Politely acknowledge and immediately redirect to job qualifications."
      },
      {
        id: 'pregnancy-2',
        candidateSays: "My husband and I are planning to start a family soon, so I'm looking for a company with good work-life balance.",
        context: 'Candidate mentions family planning intentions',
        wrongResponses: [
          "Oh, so you're planning to get pregnant? When?",
          "Are you sure this demanding role is right for you then?",
          "How soon? We need someone committed long-term."
        ],
        goodResponses: [
          {
            response: "Work-life balance is important to many of our employees. Let me tell you about our general policies, and we can discuss specific accommodations if you receive an offer.",
            explanation: "Addresses the work-life balance question without focusing on family planning"
          },
          {
            response: "I'm happy to share information about our culture. Tell me more about what you're looking for in your next role.",
            explanation: "Responds to the underlying concern while redirecting"
          },
          {
            response: "We strive to support all employees. What specific aspects of work-life balance are most important to you professionally?",
            explanation: "Addresses the topic professionally without personal inquiry"
          }
        ],
        keyTakeaway: "Never inquire about family planning. Focus on professional work-life balance aspects."
      }
    ]
  },
  {
    id: 'religion',
    title: 'Religious Practices',
    icon: Heart,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    description: 'Responding to religious practice disclosures',
    scenarios: [
      {
        id: 'religion-1',
        candidateSays: "I need to let you know that I observe the Sabbath, so I can't work from sundown Friday to sundown Saturday.",
        context: 'Candidate discloses religious observance affecting availability',
        wrongResponses: [
          "What religion are you?",
          "That's going to be a problem - we need weekend coverage.",
          "Can't you make an exception for work?"
        ],
        goodResponses: [
          {
            response: "Thank you for letting me know. Schedule accommodations are something we discuss after an offer is made. For now, let's focus on your qualifications for the role.",
            explanation: "Defers accommodation discussion to appropriate stage while redirecting to qualifications"
          },
          {
            response: "I appreciate you sharing that. We can discuss scheduling details later in the process. Tell me about your experience with [job requirement].",
            explanation: "Acknowledges without commitment, redirects to job-relevant topics"
          },
          {
            response: "Thanks for being upfront. Let's continue with the interview, and if you're selected, HR will work with you on scheduling needs.",
            explanation: "Separates accommodation from hiring decision"
          }
        ],
        keyTakeaway: "Never ask about specific religious beliefs. Defer accommodation discussions to post-offer stage."
      },
      {
        id: 'religion-2',
        candidateSays: "I wear a hijab as part of my religious practice. I hope that won't be an issue here.",
        context: 'Candidate addresses potential religious accommodation concern',
        wrongResponses: [
          "What religion requires you to wear that?",
          "We have a strict dress code here.",
          "Have you considered not wearing it at work?"
        ],
        goodResponses: [
          {
            response: "We evaluate candidates based on their qualifications and ability to do the job. Let's continue discussing your background and experience.",
            explanation: "Reassures through merit-based focus without engaging with religious topic"
          },
          {
            response: "Our focus is on finding qualified candidates. Tell me more about your professional experience.",
            explanation: "Brief reassurance followed by redirect to qualifications"
          },
          {
            response: "Thank you for sharing. What drew you to apply for this position?",
            explanation: "Simple acknowledgment with immediate redirect"
          }
        ],
        keyTakeaway: "Don't comment on or ask about religious dress. Reassure through focus on qualifications."
      }
    ]
  },
  {
    id: 'nationality',
    title: 'National Origin & Ethnicity',
    icon: Globe,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    description: 'Handling national origin and cultural background disclosures',
    scenarios: [
      {
        id: 'nationality-1',
        candidateSays: "English is actually my second language. I moved here from Korea five years ago.",
        context: 'Candidate reveals national origin and language background',
        wrongResponses: [
          "Oh interesting! Were you born in Korea?",
          "Your English is pretty good for someone who moved here recently.",
          "Do you have a green card or citizenship?"
        ],
        goodResponses: [
          {
            response: "Thank you for sharing. This role requires strong written and verbal communication. Can you tell me about your experience communicating with clients or stakeholders?",
            explanation: "Focuses on job-relevant communication skills rather than origin"
          },
          {
            response: "I appreciate the context. Let's discuss your professional experience. What relevant skills do you bring to this position?",
            explanation: "Brief acknowledgment followed by qualification-focused question"
          },
          {
            response: "Thanks for sharing. Tell me about a time you successfully communicated complex information to a diverse audience.",
            explanation: "Behavioral question focused on communication ability"
          }
        ],
        keyTakeaway: "Don't ask about country of origin or citizenship. Focus on job-related communication skills."
      },
      {
        id: 'nationality-2',
        candidateSays: "I have a strong accent because I grew up in Mexico. Sometimes people have trouble understanding me.",
        context: 'Candidate mentions accent and potential communication concern',
        wrongResponses: [
          "Yes, I noticed your accent. Where exactly in Mexico are you from?",
          "Have you taken accent reduction classes?",
          "Will clients be able to understand you?"
        ],
        goodResponses: [
          {
            response: "What matters most is effective communication. Can you give me an example of how you've successfully communicated with clients or team members in a professional setting?",
            explanation: "Focuses on demonstrated communication ability, not accent"
          },
          {
            response: "Thanks for mentioning that. Tell me about your experience working with diverse teams and clients.",
            explanation: "Redirects to professional experience with diverse groups"
          },
          {
            response: "I appreciate your awareness. Let's discuss your qualifications for the role. What experience do you have with [job requirement]?",
            explanation: "Simple acknowledgment with redirect to job requirements"
          }
        ],
        keyTakeaway: "Never comment on accents negatively. Focus on professional communication effectiveness."
      }
    ]
  },
  {
    id: 'disability',
    title: 'Disability & Health',
    icon: Accessibility,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    description: 'Responding to disability or health condition disclosures',
    scenarios: [
      {
        id: 'disability-1',
        candidateSays: "I should mention that I use a wheelchair. I want to make sure the office is accessible before we go further.",
        context: 'Candidate discloses physical disability and accessibility concern',
        wrongResponses: [
          "What happened to you?",
          "Can you do all the job duties from a wheelchair?",
          "How long have you been disabled?"
        ],
        goodResponses: [
          {
            response: "Our office is [accessible/we can discuss accessibility]. Let's focus on your qualifications - can you tell me about your experience with [job requirement]?",
            explanation: "Addresses accessibility question factually, then redirects to qualifications"
          },
          {
            response: "Thanks for asking. We're committed to providing reasonable accommodations. Tell me about your professional background and experience.",
            explanation: "Brief accommodation reassurance followed by qualification focus"
          },
          {
            response: "I can connect you with our HR team about facilities. For now, let's discuss what you would bring to this role.",
            explanation: "Refers facility questions appropriately while redirecting to qualifications"
          }
        ],
        keyTakeaway: "Answer accessibility questions directly. Don't ask about the disability itself. Focus on qualifications."
      },
      {
        id: 'disability-2',
        candidateSays: "I have depression and anxiety, but it's well-managed with medication. I've never let it affect my work.",
        context: 'Candidate discloses mental health condition',
        wrongResponses: [
          "Are you sure you can handle the stress of this job?",
          "How bad does your depression get?",
          "What medications are you on?"
        ],
        goodResponses: [
          {
            response: "Thank you for sharing. We evaluate all candidates on their qualifications and ability to perform the job. Tell me about your relevant experience.",
            explanation: "Neutral acknowledgment with focus on merit-based evaluation"
          },
          {
            response: "I appreciate your openness. Let's focus on your professional background. What accomplishments are you most proud of?",
            explanation: "Brief acknowledgment followed by positive, job-focused question"
          },
          {
            response: "Thanks for being open. Can you walk me through your experience with [specific job responsibility]?",
            explanation: "Simple acknowledgment with direct redirect to job skills"
          }
        ],
        keyTakeaway: "Never inquire about mental health details or medications. Treat as any other disclosure with brief acknowledgment and redirect."
      }
    ]
  },
  {
    id: 'marital',
    title: 'Marital & Family Status',
    icon: Users,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    description: 'Handling marital status and family information',
    scenarios: [
      {
        id: 'marital-1',
        candidateSays: "I'm a single mom with two young kids, so I really need a job with stable hours.",
        context: 'Candidate discloses family situation and schedule concern',
        wrongResponses: [
          "How old are your kids? Who watches them?",
          "Single mom? That must be tough. How will you manage?",
          "This role sometimes requires overtime - will that be a problem with your kids?"
        ],
        goodResponses: [
          {
            response: "I understand schedule predictability is important to you. Let me tell you about the typical hours for this role, and then let's discuss your qualifications.",
            explanation: "Addresses schedule concern professionally without focusing on family status"
          },
          {
            response: "Thanks for sharing what's important to you. The standard hours for this position are [X]. What experience do you have with [job requirement]?",
            explanation: "Provides schedule info, then redirects to qualifications"
          },
          {
            response: "Schedule stability is something many candidates value. Let's discuss the role requirements and your background.",
            explanation: "Validates concern generally, then pivots to job discussion"
          }
        ],
        keyTakeaway: "Don't ask about childcare arrangements. Address schedule questions professionally and redirect to qualifications."
      },
      {
        id: 'marital-2',
        candidateSays: "My wife and I just got married, and we're relocating here for her job, which is why I'm looking for work.",
        context: 'Candidate mentions spouse and relocation reason',
        wrongResponses: [
          "Oh, congratulations! What does your wife do?",
          "So you'd leave if she gets transferred again?",
          "Will her job affect your availability?"
        ],
        goodResponses: [
          {
            response: "Welcome to the area! What attracted you to this particular position?",
            explanation: "Friendly acknowledgment without spouse-focused follow-up"
          },
          {
            response: "Thanks for the context. Tell me about your professional background and what you're looking for in your next role.",
            explanation: "Brief acknowledgment with redirect to candidate's qualifications"
          },
          {
            response: "I appreciate you sharing. Let's discuss your experience and how it aligns with this role.",
            explanation: "Simple acknowledgment with job-focused redirect"
          }
        ],
        keyTakeaway: "Don't ask about spouse's job or plans. Acknowledge briefly and focus on candidate's qualifications."
      }
    ]
  }
];

// Quick reference card component
const QuickReferenceCard = ({ category }) => {
  const Icon = category.icon;
  
  return (
    <Card className={cn("border-l-4", category.borderColor)}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", category.bgColor)}>
            <Icon className={cn("h-5 w-5", category.color)} />
          </div>
          <div>
            <CardTitle className="text-lg">{category.title}</CardTitle>
            <CardDescription className="text-sm">{category.description}</CardDescription>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

// Scenario detail component
const ScenarioDetail = ({ scenario }) => {
  return (
    <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
      {/* Candidate Statement */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-blue-600" />
          <span className="font-medium text-sm text-blue-600">Candidate Says:</span>
        </div>
        <p className="text-foreground bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800 italic">
          &ldquo;{scenario.candidateSays}&rdquo;
        </p>
        <p className="text-xs text-muted-foreground">{scenario.context}</p>
      </div>

      <Separator />

      {/* Wrong Responses */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <ThumbsDown className="h-4 w-4 text-red-600" />
          <span className="font-medium text-sm text-red-600">Don&apos;t Say (Problematic):</span>
        </div>
        <ul className="space-y-1">
          {scenario.wrongResponses.map((response, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <span className="text-muted-foreground">&ldquo;{response}&rdquo;</span>
            </li>
          ))}
        </ul>
      </div>

      <Separator />

      {/* Good Responses */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <ThumbsUp className="h-4 w-4 text-emerald-600" />
          <span className="font-medium text-sm text-emerald-600">Recommended Responses:</span>
        </div>
        <div className="space-y-3">
          {scenario.goodResponses.map((item, idx) => (
            <div key={idx} className="bg-emerald-50 dark:bg-emerald-900/30 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">&ldquo;{item.response}&rdquo;</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">
                    <Lightbulb className="h-3 w-3 inline mr-1" />
                    {item.explanation}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Takeaway */}
      <div className="bg-amber-50 dark:bg-amber-900/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <span className="font-medium text-sm text-amber-800 dark:text-amber-300">Key Takeaway: </span>
            <span className="text-sm text-amber-700 dark:text-amber-400">{scenario.keyTakeaway}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function EEOCScenarioBook() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState(SCENARIO_CATEGORIES[0].id);

  const handleBackToEEOC = () => {
    navigate('/dashboard/eeoc/training');
  };

  const handleStartMockTest = () => {
    navigate('/dashboard/eeoc/mock');
  };

  return (
    <div className="space-y-6" data-testid="eeoc-scenario-book">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBackToEEOC}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to EEOC Training
          </Button>
        </div>
        <Button onClick={handleStartMockTest} className="gap-2">
          <MessageSquare className="h-4 w-4" />
          Start Mock Test
        </Button>
      </div>

      {/* Title Section */}
      <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <BookOpen className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">EEOC Scenario Reference Book</h1>
              <p className="text-purple-100 mt-1">
                Study these scenarios and responses before taking your mock test or assessment
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="space-y-2">
              <p className="font-medium text-amber-800 dark:text-amber-300">The Golden Rule of EEOC Compliance</p>
              <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1">
                <li>• <strong>Acknowledge briefly</strong> - Don&apos;t ignore what the candidate shared</li>
                <li>• <strong>Don&apos;t follow up</strong> - Never ask clarifying questions about protected information</li>
                <li>• <strong>Redirect quickly</strong> - Pivot to job-related qualifications and requirements</li>
                <li>• <strong>Stay neutral</strong> - Avoid positive or negative reactions that could show bias</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Navigation */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {SCENARIO_CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          return (
            <Button
              key={category.id}
              variant={isActive ? "default" : "outline"}
              className={cn(
                "h-auto py-3 flex flex-col items-center gap-2",
                isActive && "bg-primary text-primary-foreground"
              )}
              onClick={() => setActiveCategory(category.id)}
            >
              <Icon className="h-5 w-5" />
              <span className="text-xs text-center leading-tight">{category.title}</span>
            </Button>
          );
        })}
      </div>

      {/* Scenario Content */}
      {SCENARIO_CATEGORIES.map((category) => {
        if (category.id !== activeCategory) return null;
        
        const Icon = category.icon;
        
        return (
          <Card key={category.id}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg", category.bgColor)}>
                  <Icon className={cn("h-6 w-6", category.color)} />
                </div>
                <div>
                  <CardTitle>{category.title}</CardTitle>
                  <CardDescription>{category.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-3">
                {category.scenarios.map((scenario, idx) => (
                  <AccordionItem 
                    key={scenario.id} 
                    value={scenario.id}
                    className="border rounded-lg px-4"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-left">
                        <Badge variant="outline" className="shrink-0">
                          Scenario {idx + 1}
                        </Badge>
                        <span className="text-sm font-medium line-clamp-1">
                          {scenario.context}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ScenarioDetail scenario={scenario} categoryColor={category.color} />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        );
      })}

      {/* Summary Card */}
      <Card className="bg-slate-100 dark:bg-slate-800">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <h3 className="text-lg font-semibold">Ready to Practice?</h3>
            <p className="text-muted-foreground">
              Now that you&apos;ve studied the scenarios, put your knowledge to the test with our interactive mock interview.
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={handleBackToEEOC}>
                Return to Training
              </Button>
              <Button onClick={handleStartMockTest} className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Start Mock Test
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
